/**
 * BoothAtmosphereRibbon — iter191 ✦
 *
 * Sticky ~44px footer that gives Booth OS a living pulse without
 * a ticker, marquee, or scrolling text. Rotates softly every ~8s
 * between four ambient signals pulled from the existing per-event
 * /engagement endpoint:
 *
 *   - "127 memories saved tonight ✦"
 *   - "18 Lens Notes left tonight ✦"
 *   - "42 words dropped tonight ✦"
 *   - "9 voices recorded tonight ✦"
 *
 * iter191 — every 3rd rotation (when a latestWord is available) the
 * ribbon takes over with the emotional line:
 *
 *   "'hopeful' just drifted into the room ✦"
 *
 * Restrained — feels inhabited, not chatty. When nothing has been
 * captured, the ribbon shows the calm anchor:
 *
 *   "The room is quietly listening ✦"
 */
import React, { useEffect, useMemo, useState } from 'react';
import { SERIF } from './boothShellAtoms';

const FADE_MS = 700;
const ROTATE_MS = 8000;

const QUIET_LINE = 'The room is quietly listening ✦';

// Maps engagement counter keys → the line we render in the ribbon.
const SIGNALS = [
  { key: 'qr_upload',        format: (n) => `${n} ${n === 1 ? 'memory' : 'memories'} saved tonight ✦` },
  { key: 'lens_notes',       format: (n) => `${n} ${n === 1 ? 'Lens Note' : 'Lens Notes'} left tonight ✦` },
  { key: 'drop_one_word',    format: (n) => `${n} ${n === 1 ? 'word' : 'words'} dropped tonight ✦` },
  { key: 'video_guestbook',  format: (n) => `${n} ${n === 1 ? 'voice' : 'voices'} recorded tonight ✦` },
];

const BoothAtmosphereRibbon = ({ theme, counts, latestWord, testId = 'booth-atmosphere-ribbon' }) => {
  // Numeric signals from engagement.
  const numericMessages = useMemo(() => {
    if (!counts) return [];
    return SIGNALS
      .map((s) => ({ key: s.key, value: Number(counts[s.key] || 0), text: s.format(Number(counts[s.key] || 0)) }))
      .filter((s) => s.value >= 1);
  }, [counts]);

  // iter191 — Compose the full rotation. The drift line is interleaved
  // every 3rd slot so the ribbon feels inhabited without becoming
  // chatty. Skipped entirely when there is no latestWord.
  const messages = useMemo(() => {
    const out = [];
    let driftCountdown = 2;
    numericMessages.forEach((m) => {
      out.push(m.text);
      driftCountdown -= 1;
      if (driftCountdown <= 0 && latestWord) {
        out.push(`“${latestWord}” just drifted into the room ✦`);
        driftCountdown = 2;
      }
    });
    return out;
  }, [numericMessages, latestWord]);

  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    setIdx((cur) => (cur >= messages.length ? 0 : cur));
  }, [messages.length]);

  useEffect(() => {
    if (messages.length <= 1) return undefined;
    const swap = setInterval(() => {
      setVisible(false);
      const fade = setTimeout(() => {
        setIdx((cur) => (cur + 1) % messages.length);
        setVisible(true);
      }, FADE_MS);
      return () => clearTimeout(fade);
    }, ROTATE_MS);
    return () => clearInterval(swap);
  }, [messages.length]);

  const line = messages.length > 0 ? messages[idx] : QUIET_LINE;

  return (
    <div
      data-testid={testId}
      data-ribbon-empty={messages.length === 0 ? '1' : '0'}
      role="status"
      aria-live="polite"
      style={{
        position: 'sticky', bottom: 0, left: 0, right: 0,
        height: 44,
        zIndex: 4,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '0 18px',
        background: 'linear-gradient(180deg, rgba(20,12,6,0) 0%, rgba(15,8,3,0.86) 38%, rgba(12,6,2,0.94) 100%)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        borderTop: `1px solid ${theme?.hair || 'rgba(212,170,82,0.18)'}`,
        pointerEvents: 'none',
      }}
    >
      <span
        style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 13.5, lineHeight: 1,
          color: theme?.inkMuted || 'rgba(232,222,205,0.62)',
          letterSpacing: '0.005em',
          opacity: visible ? 1 : 0,
          transition: `opacity ${FADE_MS}ms ease`,
          textAlign: 'center',
        }}
      >
        {line}
      </span>
    </div>
  );
};

export default BoothAtmosphereRibbon;
